/**
 * Salary Service
 * 
 * Handles all API calls related to member salary management
 * Admin-only endpoints
 */

import apiClient from './apiClient';

export interface MemberSalary {
  id: number;
  user_id: number;
  email: string;
  applicable_from: string;
  monthly_salary: number;
  yearly_salary: number;
  hourly_cost: number;
  created_at?: string;
  updated_at?: string;
  firstname?: string;
  lastname?: string;
  username?: string;
}

export interface SalaryFilters {
  email?: string;
  user_id?: number;
  from_date?: string;
  to_date?: string;
  limit?: number;
  offset?: number;
}

export interface SalaryListResponse {
  success: boolean;
  data: {
    salaries: MemberSalary[];
    total: number;
    limit: number;
    offset: number;
  };
}

export interface SalaryCreateRequest {
  email: string;
  applicable_from: string;
  monthly_salary?: number;
  yearly_salary?: number;
  hourly_cost?: number;
}

export interface SalaryUpdateRequest {
  applicable_from?: string;
  monthly_salary?: number;
  yearly_salary?: number;
  hourly_cost?: number;
}

export interface ImportResult {
  success: boolean;
  data: {
    success_count: number;
    error_count: number;
    errors: Array<{
      row: number;
      email?: string;
      error: string;
    }>;
  };
}

class SalaryService {
  private readonly baseUrl = '/admin/salaries';

  /**
   * Get all salaries with optional filters
   */
  async getAllSalaries(filters?: SalaryFilters): Promise<SalaryListResponse> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.email) params.append('email', filters.email);
      if (filters.user_id) params.append('user_id', filters.user_id.toString());
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());
    }

    const url = params.toString() ? `${this.baseUrl}/?${params}` : `${this.baseUrl}/`;
    const response = await apiClient.get<SalaryListResponse>(url);
    return response.data;
  }




  /**
   * Export all salaries to CSV
   */
  async exportToCsv(): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${this.baseUrl}/export-csv`, {
      responseType: 'blob'
    });
    return response.data;
  }


  /**
   * Download CSV file from blob
   */
  downloadCsvFile(blob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `member_salaries_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new SalaryService();

